import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import parse, { domToReact } from 'html-react-parser';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import { TrackingContext } from '@vrbo/react-event-tracking';
import { Button } from '@ferris/react/components/button';
import TranslationUtil from '../../../utils/TranslationUtil';
import { createInteractionEvent, createPageViewEvent } from '../../../utils/EventProviders/EgClickstreamUtil';
import reasons from './reasons';
import ClassificationAction, { LODGIN_TYPES } from '../../../actions/classification';
import withPageFlow from '../../PageFlow/PageFlow';
import { CL_ELIGIBILITY_CHECK } from '../../PageFlow/PageList';
import { getVrboBrandByPos } from '../../../utils/BrandUtil';
import { findByLocale } from '../../../utils/PosUtil';
import LeadActions from '../../../actions/lead';


export const EligibilityPage = (props) => {
    const {
        title,
        text,
        cta,
        referrer,
        eligibilityReason,
        setVrboLodging,
        pageFlow,
        setEligibilityStatus
    } = props;

    const { trigger } = useContext(TrackingContext);

    useEffect(() => {
        setEligibilityStatus(eligibilityReason);
        window.scrollTo(0, 0);
        trigger('pageLoad', { pageName: `partner-qualifier: add-property-ineligible-${eligibilityReason}`, referrer });
        trigger('pageview', createPageViewEvent('add_property_ineligible', eligibilityReason, '2.4.0'));
        trigger('pageLoadGtm', {
            pageName: 'AddPropertyIneligible',
            event: 'AddPropertyIneligibleLoad'
        });
        return () => {
            trigger('pageUnload', { referrer });
        };
    }, []);

    const VrboRedirect = async () => {
        const gaData = {
            event: 'ClassificationData',
            id: 'ClassificationDataSubmit'
        };

        trigger('gtmEvent', gaData);
        await setVrboLodging();
        pageFlow.next();
    };

    const EPCRedirect = async () => {
        pageFlow.next();
    };

    const getButtonAction = () => {
        switch (eligibilityReason) {
        case reasons.VRBO:
            return VrboRedirect;
        case reasons.TIMEOUT:
            return EPCRedirect;
        default:
            return () => {};
        }
    };

    const onClick = () => {
        trigger('interaction', createInteractionEvent('next_button.initiated', 'add_property_ineligible', eligibilityReason, null, '2.4.0'));
        getButtonAction()();
    };

    const classes = cn('hotel-onboarding-wrapper', 'ulx-animated');

    const CtaButton = () => {
        switch (eligibilityReason) {
        case reasons.VRBO:
        case reasons.TIMEOUT:
            return (
                <Button
                    isFullWidth={false}
                    id={`${eligibilityReason}-button`}
                    onClick={onClick}
                >
                    {cta}
                </Button>
            );
        default:
            return null;
        }
    };

    return (
        <div className={classes}>
            <div className={cn('hotel-onboarding-content')}>
                <h1>{title}</h1>
                <p>{text}</p>
            </div>
            <CtaButton />
        </div>
    );
};

EligibilityPage.propTypes = {
    referrer: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    cta: PropTypes.string.isRequired,
    eligibilityReason: PropTypes.string.isRequired,
    setVrboLodging: PropTypes.func.isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func.isRequired
    }).isRequired,
    setEligibilityStatus: PropTypes.func.isRequired
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
    setVrboLodging: () => dispatch(
        ClassificationAction.setLodgingPreference(LODGIN_TYPES.VR),
    ),
    setEligibilityStatus: reason => dispatch(LeadActions.setEligibilityStatus(reason))
});

/* istanbul ignore next */
const mapStateToProps = (state, ownProps) => {
    const {
        eligibilityReason
    } = ownProps;
    const {
        app,
        lead
    } = state;
    const {
        referrer,
        locale
    } = app;
    const {
        email
    } = lead;
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);

    const getTranslation = (suffix, params = {}) => translations.getTranslation(`hotelOnboarding.eligibility.reason.${suffix}`, params);

    const vrboBrand = getVrboBrandByPos(findByLocale(locale.canonical));
    const getCta = () => {
        switch (eligibilityReason) {
        case reasons.VRBO:
            return getTranslation(`${eligibilityReason}.link`, { brand: vrboBrand });
        case reasons.TIMEOUT:
            return getTranslation(`${eligibilityReason}.link`);
        default:
            return '';
        }
    };

    const getText = () => getTranslation(
        `${eligibilityReason}.text`,
        eligibilityReason === reasons.TIMEOUT ? { 0: email } : { brand: vrboBrand }
    );

    const title = getTranslation(`${eligibilityReason}.title`, { brand: vrboBrand });
    const text = getText();

    const options = {
        replace: ({ attribs, name, children }) => {
            if (!attribs) return;
            if (name === 'a') {
                // eslint-disable-next-line consistent-return
                return (
                    <a target="_blank" href="https://www.vrbo.com/?preferlocale=true" rel="noreferrer">
                        {domToReact(children)}
                    </a>
                );
            }
        }
    };

    const parsedText = parse(text, options);

    const cta = getCta();

    return {
        referrer,
        title,
        text: parsedText,
        cta,
        email
    };
};

const EligibilityPageCL = connect(mapStateToProps, mapDispatchToProps)(EligibilityPage);
EligibilityPageCL.displayName = CL_ELIGIBILITY_CHECK;
export default hot(module)(withPageFlow(EligibilityPageCL));
